import { ISectionLogger, ISectionLoggerFactory, LoggingSection } from "@multiweb/logging";
import { AppContext } from "App/AppContext";
import { EMPTY_ITEM_RIGHTS_COLLECTION, RightType } from "Global/Base/Rights/RightTypes";
import { StandardOperationRights } from "Global/Base/Rights/StandardOperationRights";
import { loadOriginalRightsDefinition } from "UI/UI/Rights/OriginalRightsDefinitionLoader";
import { ControlRightTypesGroup, RightGroups } from "UI/UI/Rights/RightGroups";
import { DataConverter } from "../../../../../Global/Base/Converters/DataConverter";
import { ConditionParam, IOrderBy } from "../../../../../Global/Base/DataSources/Interfaces";
import { DIContainer } from "../../../../../Global/Base/DIContainer";
import { Event, IListener, StatefulEvent } from "../../../../../Global/Base/Events";
import { DeferredStateful, PromiseUtils } from "../../../../../Global/Base/Promises/PromiseUtils";
import { IRightsManager } from "../../../../../Global/Base/Rights/IRightsManager";
import { EnumUtils } from "../../../../../Global/Base/Utils/EnumUtils";
import { ConditionOperator, StringUtils } from "../../../../../Global/Base/Utils/StringUtils";
import { AttrValueReader } from "../../../../../OW.Remoting/Remoting/AttrValueReader";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWObjectList } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObjectList";
import { IWorkflowMenuItem } from "../../../../../OW.Remoting/Remoting/IWorkflowManager";
import { ActionType, IAction } from "../../../../UI.Controls/Actions/IAction";
import { IDataRowElement } from "../../../../UI.Controls/DataGridSubcontrols/Components/IDataRow";
import { SaveResultData } from "../../../../UI.Controls/DataSource/EditableDBDataSource";
import { IDataGrid } from "../../../../UI.Controls/IDataGrid";
import { SplitPanel } from "../../../../UI.ElementarControls/SplitPanel";
import { createElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { ComponentTree } from "../../../LogicalTree/ComponentTree";
import { ComponentTreeType } from "../../../LogicalTree/Definitions";
import { RightsEvaluationOverridesType, RightsManagerType } from "../../../Rights/Definitions";
import { RightsEvaluationOverrides } from "../../../Rights/RightsEvaluationOverrides";
import { FormControlWithLoggerParams } from "../../Controls/ControlInterfaces";
import { IMenu } from "../../FormUI/FormUI";
import { UIManager } from "../../UIManager";
import { DetailFrame } from "../DetailFrame";
import { FrameSelChanged, HideButtons, IActualObjectChangedEventArgs } from "../Frames";
import { IForm } from "../IForm";
import { IFrame, IFrameCtxMenuData } from "../IFrame";
import { ValidationManager } from "../ValidationManager";
import { EditStyleType, IMasterableFrame, IMasterableFrameWithDataGrid, IMultiselectComponent } from "./FrameInterfaces";
import { FrameSharedMethods } from "./FrameSharedMethods";
import { GenericListFrame } from "./GenericListFrame";
import { ITreeFrame } from "./ITreeFrame";
import { ListFrame } from "./ListFrame";
import { MasterableFormControl } from "./MasterableFormControl";
import { MasterableFrame } from "./MasterableFrame";
import { TreeViewFrame } from "./TreeViewFrame";

export class TreeFrame extends MasterableFormControl implements ITreeFrame, IMultiselectComponent {
    //#region Fields
    public readonly canBeMasterFrame: boolean = true;
    public readonly frameName: string;
    public readonly masterFrameName: string;
    public readonly onEditMethod: string;
    public readonly onCancelMethod: string;

    protected _isInitialized: boolean = false;
    protected _initializationStarted: boolean = false;

    protected _suppressSaveDialog: boolean = false;
    /**
     * Resolvovat metodou this._resolveActualObjectSetDefferred.
     * Slouží pro možnost čekání na nastavení actualObjectu.
     */
    protected _actualObjectSetDefferred: DeferredStateful<boolean> = PromiseUtils.deferStateful<boolean>();

    private _logger: ISectionLogger;

    private _treeViewFrame: TreeViewFrame;
    private _listFrame: ListFrame;

    private _refreshObjectAfterSetActive: boolean = false;
    private _refreshListAfterSetActive: boolean = false;

    private _treePanelPosition: TreePanelPositions;
    private _newObjectLevel: NewObjectLevels;
    private _hierarchyAttr: string;

    private _isMainFrame: boolean;
    protected _formCanRead: boolean | undefined = undefined;
    protected _interactive: boolean = true;
    private _detail: DetailFrame;
    private _hideToolBar: boolean;
    private _menu: IMenu;
    private _hideButtons: HideButtons;
    private _showFilterValues: boolean = false;
    private _captionElement: HTMLElement;
    protected _showCaption: boolean = false;
    /**
     * Zakáže možnost řazení
     */
    private readonly _disableSort: boolean = false;
    protected _componentTree: ComponentTreeType;
    protected __rightsEvaluationOverrides: RightsEvaluationOverridesType;
    protected _rightsManager: RightsManagerType = DIContainer.instance.create<RightsManagerType>(IRightsManager, this, this._createOrGetRightsOverrides());
    protected _rightsEvaluatedWhenNotInteractive: Set<RightType>;

    /**
     * Promise s probíhajícím vyhodnocováním práv
     */
    private _rightsEvaluationTask: Promise<void> = Promise.resolve();
    /**
     * Chain promisů s probihajicimi updaty dat
     */
    private _waitForUpdatesTask: Promise<void> = Promise.resolve();

    public readonly editModeChanged: Event<GenericListFrame, boolean> = new Event<GenericListFrame, boolean>();
    private _selectionChanged: Event<IMasterableFrame, FrameSelChanged> = new Event<IMasterableFrame, FrameSelChanged>();
    private _actualObjectChanged: Event<IMasterableFrame, IActualObjectChangedEventArgs> = new Event<IMasterableFrame, IActualObjectChangedEventArgs>();
    private _actualObjectValueChanged: Event<IMasterableFrame, OWObject> = new Event<IMasterableFrame, OWObject>();
    private _actualObjectSaved: Event<IMasterableFrame, OWObject> = new Event<IMasterableFrame, OWObject>();
    public readonly selectionModeChanged: Event<IMasterableFrame, boolean> = new Event<IMasterableFrame, boolean>();
    public keyboardSelectionChangeInitiated: Event<IMasterableFrameWithDataGrid, IDataGrid<IDataRowElement>> = new Event<IMasterableFrameWithDataGrid, IDataGrid<IDataRowElement>>();
    //#endregion Fields

    //#region Events
    public readonly contextActionsReady: Event<IMasterableFrame, string> = new Event<IMasterableFrame, string>();

    public readonly initialized: StatefulEvent<IFrame, void> = new StatefulEvent<IFrame, void>();
    public readonly frameIsVisible: Event<IFrame, any> = new Event<IFrame, any>();

    public readonly contextMenuOpening: Event<IFrame, IFrameCtxMenuData> = new Event<IFrame, IFrameCtxMenuData>();
    //#endregion Events

    //#region Listeners
    /** Listener, který v případě spuštění filtrování na seznamu zruší QueryMode na stromu */
    private _listFrameFilterExecutedListener: IListener;
    //#endregion Listeners

    //#region Properties
    public readonly uuid: string;

    protected _formObjectName: string;
    public get formObjectName(): string {
        return this._formObjectName;
    }

    public get isTreeFrame(): boolean {
        return true;
    }
    public get isMainFrame(): boolean {
        return this._isMainFrame;
    }

    public set isMainFrame(value: boolean) {
        this._isMainFrame = value;
        this._listFrame.isMainSelectionFrame = value;
    }

    public get refreshListAfterSetActive(): boolean {
        return this._listFrame.refreshListAfterSetActive || this._refreshListAfterSetActive;
    }

    public set refreshListAfterSetActive(value: boolean) {
        this._refreshListAfterSetActive = value;
    }

    public get refreshObjectAfterSetActive(): boolean {
        return this._listFrame.refreshObjectAfterSetActive || this._refreshObjectAfterSetActive;
    }

    public set refreshObjectAfterSetActive(value: boolean) {
        this._refreshObjectAfterSetActive = value;
    }

    public get formCanRead(): boolean {
        return this._formCanRead;
    }
    public set formCanRead(val: boolean) {
        this._formCanRead = val;
    }

    public get initializationStarted(): boolean {
        return this._initializationStarted;
    }

    public get isInitialized(): boolean {
        return this._isInitialized;
    }

    public get renderStandardRowActions(): boolean {
        return true;
    }

    public get canDelete(): boolean | undefined | null {
        return this._listFrame.canDelete;
    }

    public get canInsert(): boolean | undefined | null {
        return this._listFrame.canInsert;
    }

    public set canInsert(value: boolean) {
        this._listFrame.canInsert = value;
    }

    public override get visible(): boolean {
        return super.visible;
    }

    public override set visible(value: boolean) {
        super.visible = value;

        if (!this.hideToolBar && this.menu)
            this.menu.setVisibility(this.visible && this.canValueLoad);
    }

    public get interactive(): boolean {
        return this._interactive;
    }

    public set interactive(value: boolean) {
        this._interactive = value;
    }

    public get actualObjectStandardRights(): StandardOperationRights {
        return this._listFrame.actualObjectStandardRights;
    }

    public get hideButtons(): HideButtons {
        if (!this._hideButtons)
            this._hideButtons = FrameSharedMethods.parseHideButtons(this.ownerForm, this.element.getAttribute("data-hidebuttons"), this.isMainFrame);

        return this._hideButtons;
    }

    public get isActive(): boolean {
        return this.enabled && this.interactive;
    }

    public get isEditing(): boolean {
        return this._listFrame.isEditing;
    }

    public set isEditing(value: boolean) {
        this._listFrame.isEditing = value;
    }

    public get isMultiSelectingMode(): boolean {
        return this._listFrame.isMultiSelectingMode;
    }

    public set isMultiSelectingMode(v: boolean) {
        this._listFrame.isMultiSelectingMode = v;
    }

    public get isMultiSelecting(): boolean {
        return this._listFrame.isMultiSelecting;
    }
    public get canEnterMultiEdit(): boolean {
        return this._listFrame.canEnterMultiEdit;
    }
    public get isMultiEditing(): boolean {
        return this._listFrame.isMultiEditing;
    }

    public get supportsMultiEditing(): boolean {
        return this._listFrame.supportsMultiEditing;
    }

    public get hideToolBar(): boolean {
        return this._hideToolBar;
    }

    public set hideToolBar(value: boolean) {
        this._hideToolBar = value;

        if (this.menu)
            this.menu.setVisibility((!value) ? false : this.visible && this.canValueLoad);
    }

    public get detail(): DetailFrame {
        return this._detail;
    }

    public set detail(detailFrame: DetailFrame) {
        this._detail = detailFrame;
        this._listFrame.detail = detailFrame;
    }

    public get detailComponentTree(): ComponentTreeType {
        return this.detail.componentTree;
    }

    public get suppressSaveDialog(): boolean {
        return false;
    }

    public get detailForm(): string {
        return this._listFrame.detailForm;
    }

    public set detailForm(value: string) {
        this._listFrame.detailForm = value;
    }

    public override get masterFrame(): IMasterableFrame {
        return this._masterFrame;
    }

    public override set masterFrame(frame: IMasterableFrame) {
        this._masterFrame = frame;

        this._listFrame.masterFrame = frame;
        this._treeViewFrame.masterFrame = frame;
    }

    public get masterLinkToAttr(): string | null {
        return this._listFrame.masterLinkToAttr;
    }

    public set masterLinkToAttr(value: string | null) {
        this._listFrame.masterLinkToAttr = value;
        this._treeViewFrame.masterLinkToAttr = value;
    }

    public get masterAttr(): string | null {
        return this._listFrame.masterAttr;
    }

    public set masterAttr(value: string | null) {
        this._listFrame.masterAttr = value;
        this._treeViewFrame.masterAttr = value;
    }

    public get masterObject(): OWObject {
        return this._listFrame.masterObject;
    }

    public set masterObject(object: OWObject) {
        this._listFrame.masterObject = object;
        this._treeViewFrame.masterObject = object;
    }

    public get currentDataGrid(): IDataGrid<IDataRowElement> {
        return this._listFrame.currentDataGrid;
    }

    public get isChanged(): boolean {
        return this._listFrame.isChanged;
    }

    public get actualObject(): OWObject {
        return this._listFrame.actualObject;
    }

    public set actualObject(object: OWObject) {
        if (object) {
            this._listFrame.selectRow(object.id, true).then(async (sucesfullySelected) => {
                if (!sucesfullySelected)
                    await this._listFrame.loadAndSelectRecord(object.id);
            });
            this._treeViewFrame.selectRow(object.id);
        } else {
            this._listFrame.actualObject = object;
            this._treeViewFrame.actualObject = object;
        }
    }

    public get hasSelectedRecord(): boolean {
        return this._listFrame.hasSelectedRecord;
    }

    public get condition(): string {
        return this._listFrame.condition;
    }

    public set condition(value: string) {
        this._listFrame.condition = value;
        this._treeViewFrame.condition = value;
    }

    public get orderBy(): IOrderBy[] {
        return this._listFrame.orderBy;
    }

    public set orderBy(orderBy: IOrderBy[]) {
        this._listFrame.orderBy = orderBy;
        this._treeViewFrame.orderBy = orderBy;
    }

    public get conditionParamsAttrs(): readonly string[] {
        return this._listFrame.conditionParamsAttrs;
    }

    public set conditionParamsAttrs(params: readonly string[]) {
        this._listFrame.conditionParamsAttrs = params;
        this._treeViewFrame.conditionParamsAttrs = params;
    }

    public get editStyle(): EditStyleType {
        return this._listFrame.editStyle;
    }

    public set editStyle(value: EditStyleType) {
        this._listFrame.editStyle = value;
    }

    public get afterGuiCreateFill(): string {
        return this._listFrame.afterGuiCreateFill;
    }

    public set afterGuiCreateFill(value: string) {
        this._listFrame.afterGuiCreateFill = value;
    }

    public get afterGuiCreateMethod(): string {
        return this._listFrame.afterGuiCreateMethod;
    }

    public set afterGuiCreateMethod(methodName: string) {
        this._listFrame.afterGuiCreateMethod = methodName;
    }

    public get menu(): IMenu {
        return this._menu;
    }

    public set menu(menu: IMenu) {
        this._menu = menu;

        this._listFrame.menu = menu;
        this._listFrame.isMenuOwner = false;
    }

    public get componentTree(): ComponentTreeType {
        return this._componentTree;
    }

    public set componentTree(componentTree: ComponentTreeType) {
        this._componentTree = componentTree;
    }

    public get rightsManager(): RightsManagerType {
        return this._rightsManager;
    }

    public get workflowItemsChanging(): Event<MasterableFrame, IWorkflowMenuItem[]> {
        return this._listFrame.workflowItemsChanging;
    }

    public get selectionChanged(): Event<IMasterableFrame, FrameSelChanged> {
        return this._selectionChanged;
    }

    public get actualObjectChanged(): Event<IMasterableFrame, IActualObjectChangedEventArgs> {
        return this._actualObjectChanged;
    }

    public get actualObjectValueChanged(): Event<IFrame, OWObject> {
        return this._actualObjectValueChanged;
    }

    public get actualObjectSaved(): Event<IMasterableFrame, OWObject> {
        return this._actualObjectSaved;
    }

    public get dataLoadingFailed(): Event<MasterableFrame, any> {
        return this._listFrame.dataLoadingFailed;
    }

    public get dataLoadingCancelled(): Event<MasterableFrame, any> {
        return this._listFrame.dataLoadingCancelled;
    }

    public get dataLoaded(): Event<MasterableFrame, any> {
        return this._listFrame.dataLoaded;
    }

    public get changesCanceled(): Event<MasterableFrame, any> {
        return this._listFrame.changesCanceled;
    }

    public get recordChangesCanceled(): Event<MasterableFrame, string> {
        return this._listFrame.recordChangesCanceled;
    }

    public get dataGridInitialized(): StatefulEvent<IMasterableFrameWithDataGrid, IDataGrid<IDataRowElement>> {
        return this._listFrame.dataGridInitialized;
    }

    public get editingRow(): Event<IFrame, OWObject> {
        return this._listFrame.editingRow;
    }

    public get validationManager(): ValidationManager {
        return this._listFrame.validationManager;
    }

    /**
     * Vrací promis s probíhajícím vyhodnocováním práv, na které lze čekat
     */
    public get rightsEvaluationTask(): Promise<void> {
        return Promise.all([this._listFrame.rightsEvaluationTask, this._treeViewFrame.rightsEvaluationTask, this._rightsEvaluationTask]).then(() => {
            // do nothing.
        });
    }
    //#endregion Properties

    //#region Constructors
    constructor(params: FormControlWithLoggerParams) {
        super(params);
        const { ownerForm, element: el, logger } = params;

        this._logger = logger;

        this.element.classList.add("tree-frame");

        this.frameName = el.getAttribute("data-framename");

        const treePanelPositionValue = el.getAttribute("data-treepanelposition");
        this._treePanelPosition = treePanelPositionValue != null ? EnumUtils.getEnumValueFromName(TreePanelPositions, treePanelPositionValue) : TreePanelPositions.tpLeft;

        const newObjectLevelValue = el.getAttribute("data-newObjectLevel");
        this._newObjectLevel = newObjectLevelValue != null ? EnumUtils.getEnumValueFromName(NewObjectLevels, newObjectLevelValue) : NewObjectLevels.noSameLevel;

        const hierarchyAttrValue = el.getAttribute("data-hierarchyAttr");
        this._hierarchyAttr = hierarchyAttrValue || "Hierarchie";

        this.hideToolBar = this.getBoolDataAttr("hidetoolbar");
        this.objectName = this.element.getAttribute("data-objectname");
        this.masterFrameName = this.element.getAttribute("data-masterframe");
        this.onEditMethod = this.element.getAttribute("data-oneditmethod");
        this.onCancelMethod = this.element.getAttribute("data-oncancelmethod");

        this._showFilterValues = DataConverter.toBoolean(this.element.getAttribute("data-ShowFilterValues"), false);
        this._disableSort = DataConverter.toBoolean(this.element.getAttribute("data-DisableSort"), false);
        this._showCaption = DataConverter.toBoolean(this.element.getAttribute("data-showcaption"), false);

        this._transformDefinition(ownerForm, el);

        this._actualObjectSetDefferred.resolve(false);
        this.uuid = FrameSharedMethods.generateUuid(!!AppContext.instance.configuration.developmentMode, this.frameName);
    }

    public get childFrames(): IFrame[] {
        return [this._treeViewFrame, this._listFrame];
    }

    public editRecord(): Promise<boolean> {
        return this._listFrame.editRecord();
    }
    //#endregion Constructors

    //#region Methods
    /**
     * @inheritDoc
     */
    public get isSelectedAll(): boolean {
        return this._listFrame.isSelectedAll;
    }

    /**
     * @inheritDoc
     */
    public get selectedIds(): string[] {
        return this._listFrame.selectedIds;
    }

    /**
     * @inheritDoc
     */
    public get unselectedIds(): string[] {
        return this._listFrame.unselectedIds;
    }

    /**
     * @inheritDoc
     */
    public get loadingCondition(): string {
        return this._listFrame.loadingCondition;
    }

    /**
     * @inheritDoc
     */
    public get loadingConditionParams(): readonly ConditionParam[] {
        return this._listFrame.loadingConditionParams;
    }

    /**
     * @inheritDoc
     */
    public createSelectionObjectList(): OWObjectList {
        return this._listFrame.createSelectionObjectList();
    }

    public async waitForSelectedRecordChange(): Promise<boolean> {
        return false;
    }

    public async initialize(): Promise<void> {
        this._buildComponentTree();
        this._rightsManager.setComponentTree(() => this._componentTree);

        await FrameSharedMethods.runWhenFirstShown(this, () => this._initializeVisual());
    }

    /**
     * Znicializuje celý frame. Musí být zavoláno před jeho prvním použitím. Je možné spouštět asynchronní procesy.
     */
    protected async _initializeVisual(): Promise<void> {
        if (this._initializationStarted) // inicializace může proběhnout jen jednou
            return;

        this._initializationStarted = true;

        /**
         * V této metodě by neměl být žádný výkonný kód.
         * Mělo by zde být pouze volání metod inicializujících jednotlivé oblasti a takovém pořadí, jaké je obecně potřeba
         */
        try {
            UIManager.instance.showLoader();

            await this._initializeCondition();

            await this._initializeConditionParams();
            // vytvoření menu
            await this._initializeFormUI();
            // inicializace obsažených komponent (LF a TVF)
            await this._initializeSubComponents();
            // zpracování a zaregistrování akcí do menu
            await this._initializeActions();
            // inicializace práv
            await this._initializeRights();
            // navázání listenerů
            this._initializeListeners();

            this._isInitialized = true;
            this.initialized.trigger(this, undefined);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    private async _initializeCondition(): Promise<void> {
        if (this.isMainFrame) {
            let condition = "";

            // přidá se podmínka čtení pro formulář, pokud je nastavená (má přednost)
            if (this.ownerForm.formCondition)
                condition = StringUtils.combineConditions(ConditionOperator.and, condition, this.ownerForm.formCondition);

            // pokud nejsou parametry pro podmínky zároveň z formu i z framu, tak se může přidat jednoduchá framová podmínka
            if (!this.ownerForm.formConditionParamAttrs || !this.conditionParamsAttrs)
                condition = StringUtils.combineConditions(ConditionOperator.and, condition, this.condition);

            this.condition = condition;
            return;
        }
        this.condition = this.condition;
    }

    private async _initializeConditionParams(): Promise<void> {
        // pokud existuji formConditionParams, tak se uplatni pouze ty a uz se neaplikuje condition a conditionParams (neni je jak mergnout)
        if (this.ownerForm.formConditionParamAttrs && this.ownerForm.formConditionParamsObject) {
            this.conditionParamsAttrs = this.ownerForm.formConditionParamAttrs;
            return;
        }
        // aplikuje ConditionParams na hlavni frame formulare (používá to minimálně levý seznam TLF)
        this.conditionParamsAttrs = this.conditionParamsAttrs;
    }

    public async newRecord(): Promise<boolean> {
        const storedAfterGuiCreateFill = this._listFrame.afterGuiCreateFill;
        let idForHierarchyAttr = "0";
        if (this.actualObject) {
            if (this._newObjectLevel === NewObjectLevels.noChildLevel) {
                idForHierarchyAttr = await this.actualObject.id;
            } else {
                const obj = AttrValueReader.read(await this.actualObject.getValue(this._hierarchyAttr));
                if (obj && obj.id) {
                    idForHierarchyAttr = obj.id;
                }
            }
        }
        this._listFrame.afterGuiCreateFill = `${(!this._listFrame.afterGuiCreateFill) ? "" : `${this._listFrame.afterGuiCreateFill};`}${this._hierarchyAttr}=${idForHierarchyAttr}`; // `

        const result = await this._listFrame.newRecord();
        this._listFrame.afterGuiCreateFill = storedAfterGuiCreateFill;
        return result;
    }

    public cancelChanges(): Promise<void> {
        return this._listFrame.cancelChanges();
    }

    public async saveRecord(): Promise<SaveResultData[] | null> {
        if (this.actualObject)
            return await this.saveRecords(this.actualObject.id);
        return [];
    }

    /**
     * Navratove hodnoty:
     * null - ulozeni probehlo v poradku
     * prazdne pole - ulozeni se nepodarilo, ale neni co zpracovat
     * naplene pole - vraci vyjimky, vznikle pri ukladani
     * @param id Pokud je vyplněn, uloží pouze jeden záznam. V opačném případě ukládá všechny rozeditované objekty.
     */
    public async saveRecords(id?: string): Promise<SaveResultData[] | null> {
        const saveResult = await this._listFrame.saveRecords(id);
        if (saveResult == null)
            await this._treeViewFrame.refreshDataWithSettableReExpand(false);
        return saveResult;
    }

    public async createCopy(propagateError?: boolean): Promise<boolean> {
        const copyResult = await this._listFrame.createCopy(propagateError);
        if (copyResult)
            await this._treeViewFrame.refreshDataWithSettableReExpand(false, false);
        return copyResult;
    }

    public async deleteRecord(): Promise<boolean> {
        const deletedSuccesfully = await this._listFrame.deleteRecord();
        if (deletedSuccesfully)
            await this.refreshData(false);
        return deletedSuccesfully;
    }

    /**
     * Znovunacteni dat do stromu i do seznamu.
     * @param manualRefresh Jestli je akce vyvolana uzivatelem (zabranuje nebo povoluju oznaceni zaznamu ve framu)
     * @param dontReselect Zda má být objekt znovu vybrán
     */
    public async refreshData(manualRefresh: boolean, dontReselect: boolean = false): Promise<any> {
        const prevObj = this.actualObject;
        if (!this.ownerForm.queryMode || manualRefresh) // Odregistrovat udalost v pripade, ze je queryMode vypnuty nebo je vyvolan manualRefresh
            this._listFrame.filterExecuted.remove(this._listFrameFilterExecutedListener);

        return Promise.all([this._listFrame.refreshData(manualRefresh), this._treeViewFrame.refreshDataWithSettableReExpand(manualRefresh, false)]).then(() => {
            if (!dontReselect)
                this.actualObject = prevObj;
        });
    }

    public async refreshActualObject(): Promise<void> {
        await this._listFrame.refreshActualObject();
    }

    public prevRow(): void {
        this._listFrame.prevRow();
    }

    public nextRow(): void {
        this._listFrame.nextRow();
    }

    public triggerActualObjectValueChange(actualObject: OWObject): void {
        return this._listFrame.triggerActualObjectValueChange(actualObject);
    }

    /**
     * Zaregistruje probihajici zmenu dat
     */
    public async enqueuePendingUpdateTask(task: Promise<void>): Promise<void> {
        // je nutne predat na listFrame, ktery vyvolava ulozeni
        this._listFrame.enqueuePendingUpdateTask(task);
        return this._waitForUpdatesTask = this._waitForUpdatesTask.then(() => task);
    }

    /**
     * Do fronty s vyhodnocováním práv přidá další ukol, který se provede, až doběhnou úkoly předchozí
     * @param task Vrací promis, na který lze čekat
     */
    public async enqueueRightsEvaluationTask(task: Promise<void>): Promise<void> {
        this._listFrame.enqueueRightsEvaluationTask(task);
        return this._rightsEvaluationTask = this._rightsEvaluationTask.then(() => task);
    }

    public getWorkflowMenuItems(): IWorkflowMenuItem[] {
        return this._listFrame.getWorkflowMenuItems();
    }

    public async validate(): Promise<boolean> {
        return this._listFrame.validate();
    }

    public async validateAndShowMessage(): Promise<boolean> {
        return this._listFrame.validateAndShowMessage();
    }

    public async waitForDataLoad(): Promise<boolean> {
        const results = await Promise.all([this._listFrame.waitForDataLoad(), this._treeViewFrame.waitForDataLoad()]);
        return results.every(result => result);
    }

    /**
     * Počká na dokončení probíhajícího nastavení ActualObjectu na všech nadřízených framech a potom na tomto
     */
    public async waitForActualObjectSet(): Promise<boolean> {
        if (this.masterFrame)
            await this.masterFrame.waitForActualObjectSet();

        const results = await Promise.all([this._listFrame.waitForActualObjectSet(), this._treeViewFrame.waitForActualObjectSet()]);

        return results.every(result => result);
    }

    public async waitForPendingUpdates(): Promise<void> {
        await Promise.all([this._listFrame.waitForPendingUpdates(), this._waitForUpdatesTask]);
    }

    /**
     * Vrací, zda je předaný frame přímou subkomponentou(LF nebo TVF) TreeFramu
     */
    public isSubframe(frame: IMasterableFrame): boolean {
        return this._listFrame === frame || this._treeViewFrame === frame;
    }

    /**
     * Nastaveni akci pro Toolbary
     */
    protected async _initializeFormUI(): Promise<void> {
        this.menu = this.ownerForm.formUI.createFrameMenu(this);
        let action: IAction;

        //Registrace akce refresh na toolbaru. Není v metodě _initializeActions, protože v té fázi už do menu přidal akci LF.
        //Prvni cast podminky kontroluje zda je tlacitko viditelne. Druha cast rika, ze pokud se seznam lezi na formulari, ktery ma nastaveno VisibleOnlyDetail
        //a soucasne jeho parent je form, tak akci nezaregistruje a nechaji, aby si ji zaregistroval DetailFrame. Je to z duvodu refreshe na modalnim okne, ktere nema
        //viditelny seznam. V tomto pripade je detail navazan na seznam, ze ktereho bylo modalni okno otevrene a ne na skryty seznam na modalnim okne.
        if (!this.hideButtons.refresh && !(this.isMainFrame && this.ownerForm.visibleOnlyDetail)) {
            action = FrameSharedMethods.createStandardAction(ActionType.standardRefreshList, () => this.refreshData(true), this);
            // Nastaveno napevno, při inicializaci se nevolá vyhodnocení práv, aby se na dílčích framech nevyhodnotila zbytečně dvakrát
            action.enabled = true;
            action.enabledVisual = true;
            this.menu.addAction(action);
        }

        // Jestli na formuláři zobrazuje jen seznam, a editovat ho musí v samostatném okně (property "ShowOnlyOutside" na DetailFrame je True)
        if (!this.ownerForm.visibleOnlyDetail && (this.ownerForm.detailFrame == null || (/*TODO: odstranit natypování až bude masterFrame typu IMasterFrame*/ this.ownerForm.detailFrame.masterFrame as IMasterableFrame) !== this)) {
            if (!this.hideButtons.insert) {
                if ((action = FrameSharedMethods.createStandardAction(ActionType.standardNewRecord, async () => {
                    await this.newRecord();
                }, this)) != null) {
                    action.enabled = false;
                    this.menu.addAction(action);
                }
            }

            if (!this.hideButtons.copy) {
                if ((action = FrameSharedMethods.createStandardAction(ActionType.standardCopyRecord, async () => {
                    await this.createCopy();
                }, this)) != null)
                    this.menu.addAction(action);
            }

            if (!this.hideButtons.edit) {
                if ((action = FrameSharedMethods.createStandardAction(ActionType.standardEditRecord, async () => {
                    await this.editRecord();
                }, this)) != null)
                    this.menu.addAction(action);
            }

            if (!this.hideButtons.save) {
                if ((action = FrameSharedMethods.createStandardAction(ActionType.standardSaveRecord, async () => {
                    await this.saveRecords();
                }, this)) != null)
                    this.menu.addAction(action);
            }
            if (!this.hideButtons.cancel) {
                if ((action = FrameSharedMethods.createStandardAction(ActionType.standardCancelChanges, () => this.cancelChanges(), this)) != null)
                    this.menu.addAction(action);
            }

            if (!this.hideButtons.delete) {
                if ((action = FrameSharedMethods.createStandardAction(ActionType.standardDeleteRecord, async () => {
                    await this.deleteRecord();
                }, this)) != null)
                    this.menu.addAction(action);
            }
        }

        this._createFrameCaption();
    }

    /** Vytvoří popisek, pokud je zadán lokalizační klíč */
    protected _createFrameCaption(): void {
        if (this.locKey && !this.locKey.endsWith("-666")) { // Element caption chceme vložit do stránky jen pokud je co zobrazit
            this.element.classList.add("with-frame-caption");
            this._captionElement = FrameSharedMethods.createFrameCaptionElement(this.locKey);
            this.element.insertBefore(this._captionElement, this.element.firstElementChild);
            this._setFrameCaptionVisibility(this._showCaption);
        }
    }

    /** Podle showCaption nastavuje viditelnost elementu frame-caption. */
    protected _setFrameCaptionVisibility(value: boolean): void {
        this._captionElement?.classList.toggle("hidden", !value);
    }

    protected async _initializeRights(): Promise<void> {
        this.componentTree = null;
        this._buildComponentTree();

        await this._newEvaluateRightsGroup(ControlRightTypesGroup.IED | ControlRightTypesGroup.initialized);
    }

    protected _buildComponentTree(): void {
        this.__rightsEvaluationOverrides = this._createOrGetRightsOverrides();

        this._componentTree = ComponentTree.buildComponentTree(this, {
            frameContext: this.frameContext,
            rightsEvaluationOverrides: this.__rightsEvaluationOverrides,
            isPreinitialized: !!this.componentTree,
        });
    }

    protected _createOrGetRightsOverrides(): RightsEvaluationOverridesType {
        if (!this.__rightsEvaluationOverrides)
            this.__rightsEvaluationOverrides = new RightsEvaluationOverrides(this);

        return this.__rightsEvaluationOverrides;
    }

    protected async _initializeActions(): Promise<void> {
        let action: IAction;

        if (!this._listFrame.actualObjectStandardRights.canNew) {
            if (!this.hideButtons.insert) {
                if ((action = this.menu.getAction(ActionType.standardNewRecord)) != null)
                    action.enabled = false;
            }
            if (!this.hideButtons.copy) {
                if ((action = this.menu.getAction(ActionType.standardCopyRecord)) != null)
                    action.enabled = false;
            }
        }

        if (!this.isEditing) {
            if (!this.hideButtons.save) {
                if ((action = this.menu.getAction(ActionType.standardSaveRecord)) != null)
                    action.visible = false;
            }
            if (!this.hideButtons.cancel) {
                if ((action = this.menu.getAction(ActionType.standardCancelRecordChanges)) != null)
                    action.visible = false;
                if ((action = this.menu.getAction(ActionType.standardCancelChanges)) != null)
                    action.visible = false;
            }
        }
    }

    protected _initializeListeners(): void {
        // predani udalosti z ListFramu
        this._listFrame.editModeChanged.add((sender, value) => {
            this.editModeChanged.trigger(sender, value);
        });

        this._listFrame.selectionChanged.mergeWith(this.selectionChanged);
        this._selectionChanged = this._listFrame.selectionChanged;

        this._listFrame.actualObjectSaved.mergeWith(this.actualObjectSaved);
        this._actualObjectSaved = this._listFrame.actualObjectSaved;

        this._listFrame.actualObjectChanged.mergeWith(this._actualObjectChanged);
        this._actualObjectChanged = this._listFrame.actualObjectChanged;

        this._listFrame.keyboardSelectionChangeInitiated.mergeWith(this.keyboardSelectionChangeInitiated);
        this.keyboardSelectionChangeInitiated = this._listFrame.keyboardSelectionChangeInitiated;

        this._treeViewFrame.selectionChanged.add(async (_sender, changeArgs) => {
            if (await this.ownerForm.checkAndSaveForm({ sourceFrame: (this.editStyle === EditStyleType.esMulti) ? this : null, forceSave: this._suppressSaveDialog })) {
                if (changeArgs.actualObject && (this._listFrame.actualObject && this._listFrame.actualObject.id) !== changeArgs.actualObject.id)
                    if (!(await this._listFrame.selectRow(changeArgs.actualObject.id, true))) {
                        await this._listFrame.loadAndSelectRecord(changeArgs.actualObject.id);
                    }
            }
        });

        this.selectionChanged.add((sender, changeArgs) => {
            if (changeArgs.actualObject && !changeArgs.actualObject.isNew && sender !== this._treeViewFrame && ((this._treeViewFrame.actualObject && this._treeViewFrame.actualObject.id) !== changeArgs.actualObject.id)) { // Ošetření, aby změna z TVW cyklicky neměnila svůj označený záznam
                this._treeViewFrame.selectRow(changeArgs.actualObject.id);
            } else if (changeArgs.actualObject?.isNew) {
                // Pokud se jedná o novou instanci, v TreeViewFramu ještě neexistuje, takže na něm akorát zrušíme výběr řádku.
                this._treeViewFrame.selectRow("-1");
            }
        });

        this._treeViewFrame.contextActionsReady.add((_, obj) => this.contextActionsReady.trigger(this, obj));

        this.actualObjectSaved.add(async (_sender, object) => {
            await this.refreshData(false, true);
            this._treeViewFrame.selectRow(object.id);
        });

        if (!this.masterFrame && this.ownerForm.queryMode) {
            this._listFrame.setToQueryMode(() => this.refreshData(true));
            this._treeViewFrame.dataGrid.setToQueryMode(() => this.refreshData(true));

            this._listFrame.filterExecuted.add(this._listFrameFilterExecutedListener = () => {
                this._treeViewFrame.refreshData(true);

                this._listFrame.filterExecuted.remove(this._listFrameFilterExecutedListener);
            });
        }
    }

    protected _resolveActualObjectSetDefferred(value: boolean): void {
        if (!this._actualObjectSetDefferred.isResolved)
            this._actualObjectSetDefferred.resolve(value);
    }

    /**
     * Převede definici TreeFramu na SplitPanel obsahující TreeViewFrame a ListFrame
     */
    private _transformDefinition(ownerForm: IForm, element: HTMLElement): void {
        let elementForListFrame = createElement();
        elementForListFrame.innerHTML = element.outerHTML;
        elementForListFrame = elementForListFrame.firstChild as HTMLElement;

        this._removeElementAttributes(elementForListFrame, [
            "data-behaviour", // Chci, aby nebyl z pohledu kódu z Formu "vidět" jako frame, aby se např. neprováděla inicializace způsobem odpovídajícím framu bez masterFramu
            "data-showcaption", // V ListFramu nechceme zobrazovat caption.
            "data-addAttrsListMethod",
            "data-treeemptymessagelc",
            "data-treeemptymessageicon",
        ]);

        const listframeFrameName = elementForListFrame.getAttribute("data-framename");
        if (listframeFrameName) {
            elementForListFrame.setAttribute("data-framename", `${listframeFrameName}_${ListFrame.name}`);
        }
        if (elementForListFrame.hasAttribute("id")) {
            const lfIdValue = `${elementForListFrame.getAttribute("id")}-lf`; // nastaví id postfix "-lf", aby byly id unikátní, ale zůstala možnost výběru
            elementForListFrame.setAttribute("id", lfIdValue);
        }
        if (elementForListFrame.hasAttribute("data-tsccontrol")) {
            elementForListFrame.setAttribute("data-tsccontrol", ListFrame.name /*"ListFrame"*/);
        }
        const composites = this.element.getAttribute("data-composites");
        if (composites)
            elementForListFrame.setAttribute("data-composites", composites);
        const treeAttributesElement = elementForListFrame.querySelector("span[data-tscitem='TreeAttributes']");
        if (treeAttributesElement) {
            treeAttributesElement.remove();
        }

        const originalRightsDefinition = loadOriginalRightsDefinition(elementForListFrame);

        this._listFrame = new ListFrame({
            ownerForm,
            element: elementForListFrame,
            originalRightsDefinition,
        });
        this._listFrame.selectionModeChanged.add((_, prm) => this.selectionModeChanged.trigger(this, prm));
        this._listFrame.showFilterValues = this._showFilterValues;
        this._listFrame.disableSort = this._disableSort;
        this._listFrame.disableGrouping = true;
        this._listFrame.addCanEditPredicate(() => {
            return true;
        });

        let elementForTreeViewFrame = createElement();
        elementForTreeViewFrame.innerHTML = element.outerHTML;
        elementForTreeViewFrame = elementForTreeViewFrame.firstChild as HTMLElement;

        const treeEmptyMessageIcon = elementForTreeViewFrame.getAttribute("data-treeemptymessageicon");
        const treeEmptyMessageLC = elementForTreeViewFrame.getAttribute("data-treeemptymessagelc");

        this._removeElementAttributes(elementForTreeViewFrame, [
            "data-showcaption",
            "data-behaviour",
            "data-showcaption",
            "data-treeemptymessageicon",
            "data-treeemptymessagelc",
            "data-emptymessageicon",
            "data-emptymessagelc",
            "data-emptyfiltermessageicon",
            "data-emptyfiltermessagelc",
            "data-emptyfulltextmessageicon",
            "data-emptyfulltextmessagelc",
        ]);

        if (treeEmptyMessageIcon) {
            elementForTreeViewFrame.setAttribute("data-emptymessageicon", treeEmptyMessageIcon);
        }
        if (treeEmptyMessageLC) {
            elementForTreeViewFrame.setAttribute("data-emptymessagelc", treeEmptyMessageLC);
        }
        const treeViewFrameName = elementForTreeViewFrame.getAttribute("data-framename");
        if (treeViewFrameName) {
            elementForTreeViewFrame.setAttribute("data-framename", `${treeViewFrameName}_${TreeViewFrame.name}`);
        }
        if (elementForTreeViewFrame.hasAttribute("id")) {
            const tvfIdValue = `${elementForTreeViewFrame.getAttribute("id")}-tvf`; // ` nastaví id postfix "-tvf", aby byly id unikátní, ale zůstala možnost výběru
            elementForTreeViewFrame.setAttribute("id", tvfIdValue);
        }
        if (elementForTreeViewFrame.hasAttribute("data-tsccontrol")) {
            elementForTreeViewFrame.setAttribute("data-tsccontrol", TreeViewFrame.name /*"TreeViewFrame"*/);
        }
        const attributesElement = elementForTreeViewFrame.querySelector("span[data-tscitem='Attributes']");
        if (attributesElement) {
            attributesElement.remove();
        }
        const treeViewRightsElements = elementForTreeViewFrame.querySelectorAll("span[data-tscitem^=\"Rights.\"]");
        treeViewRightsElements.forEach(rightElement => rightElement.remove());

        const sectionLogger = DIContainer.instance.create(ISectionLoggerFactory).create(LoggingSection.treeViewFrame);
        this._treeViewFrame = new TreeViewFrame({
            ownerForm,
            element: elementForTreeViewFrame /*, this*/,
            originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION,
            logger: sectionLogger,
        });
        this._treeViewFrame.hideToolBar = true;
        this._treeViewFrame.disableSort = this._disableSort;

        const splitPanel = new SplitPanel();
        splitPanel.id = this.element.id + "-splitpanel";
        const minSizeValue = element.getAttribute("data-minsize");
        if (minSizeValue)
            splitPanel.minSize = DataConverter.toNumber(minSizeValue);
        const splitterPosition = element.getAttribute("splitter-position");
        if (splitterPosition)
            splitPanel.splitterPosition = +splitterPosition;
        else
            splitPanel.splitterPosition = this._treePanelPosition === TreePanelPositions.tpLeft ? 20 : 80;
        splitPanel.appendChild(elementForTreeViewFrame);
        elementForTreeViewFrame.insertAdjacentElement(this._treePanelPosition === TreePanelPositions.tpLeft ? "afterend" : "beforebegin", elementForListFrame); // Vloží ListFrame vpravo nebo vlevo od TreeView podle nastavení

        // Odstraní všechny podelementy elementy, kromě právových
        for (const child of Array.from(this.element.children)) {
            if (!Array.from(child.attributes).some(elementAttribute => elementAttribute.value.indexOf("Rights.") > -1))
                child.remove();
        }

        this.element.appendChild(splitPanel);
    }

    private _removeElementAttributes(element: Element, attributesToRemove: string[]): void {
        attributesToRemove.forEach(attr => {
            if (element.hasAttribute(attr)) {
                element.removeAttribute(attr);
            }
        });
    }

    /**
     * Spustí postupně inicializaci obsaženého ListFramu a TreeViewFramu a počká na jejich doběhnutí
     */
    private async _initializeSubComponents(): Promise<void> {
        if (this._listFrame) {
            if (this.masterFrame)
                this._listFrame.masterFrame = this.masterFrame;
            await this._listFrame.initialize();
            if (this.onEditMethod) this._listFrame.onEditMethod = this.onEditMethod;
            if (this.onCancelMethod) this._listFrame.onCancelMethod = this.onCancelMethod;
        } else
            this._logger.error(`ListFrame subcomponent missing! Frame name: ${this.frameName}.`); // `
        if (this._treeViewFrame) {
            if (this.masterFrame)
                this._treeViewFrame.masterFrame = this.masterFrame;
            await this._treeViewFrame.initialize();
        } else
            this._logger.error(`TreeViewFrame subcomponent missing! Frame name: ${this.frameName}.`); // `
    }

    protected async _newEvaluateRights(rightTypes: RightType[], priorityRightTypes?: RightType[]): Promise<void> {
        const rightsEvaluationWrappedPromise = (async () => {
            await this.waitForActualObjectSet();
            if (this._isDisposed) {
                // Může nastat situace, kdy dojde k dispose TreeFrame ještě před vyhodnocením práv. V takovém případě už nelze práva vyhodnocovat, protože je disposed i rights manager.
                return Promise.resolve();
            } else {
                return this._rightsManager.evaluateRights(rightTypes, this.actualObject, this.isMainFrame, priorityRightTypes);
            }
        })();

        //Pokud frame není interaktivní, nemá smysl vyhodnocovat práva. Práva je ale potřeba vyhodnotit, pokud se stane interaktivní
        if (!this._interactive) {
            if (this._rightsEvaluatedWhenNotInteractive)
                this._rightsEvaluatedWhenNotInteractive = new Set([...Array.from(this._rightsEvaluatedWhenNotInteractive), ...rightTypes]);
            else
                this._rightsEvaluatedWhenNotInteractive = new Set(rightTypes);
        }

        this.enqueueRightsEvaluationTask(rightsEvaluationWrappedPromise);

        return rightsEvaluationWrappedPromise;
    }

    protected async _newEvaluateRightsGroup(rightGroup: ControlRightTypesGroup): Promise<void> {
        await this._newEvaluateRights(RightGroups.getControlRightGroupTypes(rightGroup), RightGroups.getControlPriorityRightGroupTypes(rightGroup));
    }

    protected async _evaluateInteractiveRights(): Promise<void> {
        //pokud frame nebyl interaktivní, místo skutečného vyhodnocení práv si ukládál, která práva bude potřeba znovu vyhodnotit, až se stane interaktivní.
        const pendingRightsGroups = this._rightsEvaluatedWhenNotInteractive ? Array.from(this._rightsEvaluatedWhenNotInteractive) : undefined;
        await this._newEvaluateRights([RightType.interactive], pendingRightsGroups);
    }

    public canBeEnabled(actionType: ActionType): boolean {
        return this._listFrame.canBeEnabled(actionType);
    }

    public override dispose(): void {
        if (this._isDisposed) {
            return;
        }

        this._listFrame?.dispose();
        this._treeViewFrame?.dispose();
        this.rightsManager.dispose();
        super.dispose();
    }
}
//#endregion Methods

enum TreePanelPositions {
    tpLeft,
    tpRight,
}

enum NewObjectLevels {
    noSameLevel,
    noChildLevel,
}
